import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of, Subject, throwError, timer } from 'rxjs';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AuthConstants, AuthErrorMessages } from '@app/constants/application-constants';
import { Contact, ContactRegistration } from '@app/model/contact.model';
import { RestURLsEnum } from '@app/model/http-request-enums';
import { Store } from '@ngrx/store';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { catchError, delay, map, mergeMap, retry, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ResetOrderInformation } from '../order-checkout/store/order-checkout/order-checkout.actions';
import * as fromApp from '../store/app.reducers';
import { ClearUserProfile, SetLoggedIn, SetLoginError, UserInfoLoaded } from '../store/auth/auth.actions';
import {
    ClearDeliveryAddressSuggestions, ResetLoggedInContact
} from '../store/labsys-contact/labsys-contact.actions';
import { ContactService } from './contact.service';
import { EMPTY_PROFILE, Profile } from './profile.model';
import { AuthToken } from '@app/model/auth/auth-token.model';
import { AuthResult } from '@app/model/auth/auth-result.model';
import { jwtDecode } from 'jwt-decode';
import { AuthClient, LoginRequest, LoginResponse, RegisterRequest, RegisterResponse, AddressRegistrationRest,
    ChangePasswordRequest, ChangePasswordResponse } from '@app/clients/client.generated';

@Injectable()
export class AuthService {

    // when the profile changes save the new profile in the subject
    userProfile$ = new BehaviorSubject<Profile>(EMPTY_PROFILE);

    // Create a stream of logged in status to communicate throughout app
    loggedIn: boolean;
    loggedIn$ = new BehaviorSubject<boolean>(false);

    public static getAuthTokenFromLocalStorage(): AuthToken {
        const authTokenLocalStorageItem = localStorage.getItem(AuthConstants.AUTH_TOKEN_LOCAL_STORAGE_KEY);

        if (authTokenLocalStorageItem === null || authTokenLocalStorageItem === undefined) {
            return null;
        }

        return JSON.parse(authTokenLocalStorageItem) as AuthToken;
    }

    public static getAccessToken(): string {
        const authToken = AuthService.getAuthTokenFromLocalStorage();

        return authToken ? authToken.accessToken : null;
    }

    public static getProfileFromLocalStorage(): Profile {
        const profileLocalStorageItem = localStorage.getItem(AuthConstants.PROFILE_LOCAL_STORAGE_KEY);

        if (profileLocalStorageItem === null || profileLocalStorageItem === undefined) {
            return null;
        }

        return JSON.parse(profileLocalStorageItem) as Profile;
    }

    public static getLoggedInContactFromLocalStorage(): Contact {
        const contactLocalStorageItem = localStorage.getItem(AuthConstants.LOGGED_IN_CONTACT_LOCAL_STORAGE_KEY);

        if (contactLocalStorageItem === null || contactLocalStorageItem === undefined) {
            return null;
        }

        return JSON.parse(contactLocalStorageItem) as Contact;
    }

    constructor(public router: Router,
        public route: ActivatedRoute,
        private store: Store<fromApp.AppState>,
        private httpClient: HttpClient,
        private contactService: ContactService,
        private notification: NzNotificationService,
        private location: Location,
        private authClient: AuthClient) {
    // The storage event will only fire when another session (in another tab or window) changes localStorage.
    // The following handler will therefore ensure that whenever a user logs in/out in a given session, the same will
    // automatically happen in any other open sessions.
        window.addEventListener('storage', ev => {
            if (ev.key === AuthConstants.AUTH_TOKEN_LOCAL_STORAGE_KEY && ev.storageArea === localStorage) {
                // Delay the reloading of the window by 2 seconds to avoid race conditions in setting localStorage
                setTimeout(() => {
                    if (ev.newValue === null) {
                        this.logoutAndNavigate(this.getAuthenticationURLEntry());
                    } else {
                        window.location.href = this.getAuthenticationURLEntry();
                        window.location.reload();
                    }
                }, 2000);
            }
        });
    }

    public login(): void {
        this.router.navigate(['/account/login']);
    }

    public saveAuthTokenToLocalStorage(authResult: AuthResult): AuthToken {
        const authTokenString = JSON.stringify({
            accessToken: authResult.accessToken,
            idToken: authResult.idToken,
            expiresAt: jwtDecode(authResult.accessToken).exp
        });
        localStorage.setItem(AuthConstants.AUTH_TOKEN_LOCAL_STORAGE_KEY, authTokenString);

        return AuthService.getAuthTokenFromLocalStorage();
    }

    public logout(): void {
        this.prepareLogout();

        const landingPage = environment.appLandingPage;
        this.router.navigate(['']);
    }

    public logoutAndNavigate(path: string, forceRefresh: boolean = false): void {
        this.prepareLogout();
        if (forceRefresh) {
            // This ensures that any open modals are forced closed
            this.location.go(path);
            window.location.reload();
        } else {
            this.router.navigate([path]);
        }
    }

    public clearContactDetails(options = { clearCart: false }) {
        this.store.dispatch(new SetLoggedIn(false, options.clearCart));
        // Go back to the home route
        this.store.dispatch(new ResetLoggedInContact());
        this.store.dispatch(new ClearDeliveryAddressSuggestions());
        this.store.dispatch(new ResetOrderInformation());
    }

    public clearLocalStorageValues() {
    // Remove tokens and expiry time from localStorage
        localStorage.removeItem(AuthConstants.AUTH_TOKEN_LOCAL_STORAGE_KEY);
        localStorage.removeItem(AuthConstants.PROFILE_LOCAL_STORAGE_KEY);
        localStorage.removeItem(AuthConstants.LOGGED_IN_CONTACT_LOCAL_STORAGE_KEY);
        // Remove these legacy auth entries
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('expires_at');
    }

    public isAuthenticated(): boolean {
        const token = AuthService.getAuthTokenFromLocalStorage();

        return (token != null) && ((Date.now() / 1000) < token.expiresAt);
    }

    public resetPassword(email = this.userProfile$.getValue().email) {
        // This method is deprecated - use resetPasswordWithMyLabAPI instead
        this.notification.info('Password Reset', 'Please use the password reset form on the login page.');
        this.router.navigate(['/account/reset-password']);
    }

    public handleAuthenticationURLEntry(path: string = '/') {
        if (this.isAuthenticated()) {
            localStorage.removeItem('auth-entry-url');
        } else {
            if (path !== '/account/login' && path !== '/account/create') {
                // don't redirect to login or registration pages after successful auth
                localStorage.setItem('auth-entry-url', path);
            }
        }
    }

    public getAuthenticationURLEntry() {
        const path = '/';
        const entryPath = localStorage.getItem('auth-entry-url');
        return !!entryPath ? entryPath : path;
    }

    public accountLogin(username, password, cb) {
        // This method is deprecated - use loginWithMyLabAPI instead
        console.warn('accountLogin is deprecated. Use loginWithMyLabAPI instead.');
        if (cb) {
            cb(new Error('This login method is no longer supported. Please use the new login form.'), null);
        }
    }

    /**
     * New login method using the MyLab API endpoints
     */
    public loginWithMyLabAPI(email: string, password: string): Observable<LoginResponse> {
        const loginRequest = new LoginRequest({
            email: email,
            password: password
        });

        return this.authClient.login(loginRequest).pipe(
            tap((response: LoginResponse) => {
                const authToken = {
                    accessToken: response.access_token,
                    idToken: response.id_token,
                    expiresAt: Math.floor(Date.now() / 1000) + (response.expires_in || 86400)
                };

                const authTokenString = JSON.stringify(authToken);
                localStorage.setItem(AuthConstants.AUTH_TOKEN_LOCAL_STORAGE_KEY, authTokenString);
            }),
            catchError((error) => {
                console.error('Login failed:', error);
                return throwError(error);
            })
        );
    }

    public register(contactRegistration: ContactRegistration): Observable<any> {
        const url = `${environment.registerHost}${RestURLsEnum.REGISTER}`;
        return this.httpClient.post(url, contactRegistration);
    }

    /**
     * New register method using the MyLab API endpoints
     */
    public registerWithMyLabAPI(contactRegistration: ContactRegistration): Observable<RegisterResponse> {
        const registerRequest = new RegisterRequest({
            firstName: contactRegistration.firstName,
            lastName: contactRegistration.lastName,
            email: contactRegistration.email,
            password: contactRegistration.password,
            company: contactRegistration.company,
            mobilePhone: contactRegistration.mobilePhone,
            homePhone: contactRegistration.homePhone,
            physicalAddress: new AddressRegistrationRest({
                line1: contactRegistration.physicalAddress.line1,
                line2: contactRegistration.physicalAddress.line2,
                townOrCity: contactRegistration.physicalAddress.townOrCity,
                postCode: contactRegistration.physicalAddress.postCode,
                province: contactRegistration.physicalAddress.province,
                country: contactRegistration.physicalAddress.country,
                nzPostDpId: contactRegistration.physicalAddress.nzPostDpId
            }),
            postalAddress: new AddressRegistrationRest({
                line1: contactRegistration.postalAddress.line1,
                line2: contactRegistration.postalAddress.line2,
                townOrCity: contactRegistration.postalAddress.townOrCity,
                postCode: contactRegistration.postalAddress.postCode,
                province: contactRegistration.postalAddress.province,
                country: contactRegistration.postalAddress.country,
                nzPostDpId: contactRegistration.postalAddress.nzPostDpId
            }),
            portalTcAccepted: contactRegistration.portalTcAccepted
        });

        return this.authClient.register(registerRequest).pipe(
            catchError((error) => {
                console.error('Registration failed:', error);
                return throwError(error);
            })
        );
    }

    private prepareLogout(): void {
    // order matters 1. clear session credentials first then 2. setting loggedin to false
    // there is an effect to reload the cart everytime there is a change in loggedin value
        this.clearLocalStorageValues();
        this.clearContactDetails({ clearCart: true });
        this.store.dispatch(new SetLoggedIn(false));
        this.store.dispatch(new ClearUserProfile());
    }

    public setOptInAsGuest(): void {
        const expiryTime = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        const now = new Date().getTime();
        const item = {
            expiry: now + expiryTime
        };
        localStorage.setItem(AuthConstants.OPT_AS_GUEST_STORAGE_KEY, JSON.stringify(item));
    }

    public getOptInAsGuest(): any {
        const isGuestOptInStr = localStorage.getItem(AuthConstants.OPT_AS_GUEST_STORAGE_KEY);
        if (!isGuestOptInStr) {
            return null;
        }

        const isGuestOptInObj = JSON.parse(isGuestOptInStr);
        const now = new Date().getTime();

        if (now > isGuestOptInObj.expiry) {
            localStorage.removeItem(AuthConstants.OPT_AS_GUEST_STORAGE_KEY);
            return null;
        }

        return isGuestOptInObj;
    }

    public clearOptInAsGuest(): void {
        localStorage.removeItem(AuthConstants.OPT_AS_GUEST_STORAGE_KEY);
    }

}
